import { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { GridValidRowModel } from '@mui/x-data-grid-pro'
import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { PillProps } from 'ext-ui/components/Pill/Pill'
import _ from 'lodash'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Box from 'src/components/Box/Box'
import CardGraphicAndNameCell from 'src/components/DataGrid/cells/CardGraphicAndNameCell/CardGraphicAndNameCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import UserAvatarCell from 'src/components/DataGrid/cells/UserAvatarCell/UserAvatarCell'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import ActionsCell from 'src/components/DataGrid/tables/PhysicalCardsTable/cells/ActionsCell/ActionsCell'
import UtilityToolbar from 'src/components/DataGrid/UtilityToolbar/UtilityToolbar'
import { FiltersFlyoutProps } from 'src/components/FilterSection/FilterSection'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import SvgMoreIcon from 'src/components/Svgs/SvgMoreIcon'
import {
  ExtendedCreditCardListModel,
  useCreditCardsWithStats,
} from 'src/hooks/creditCardHooks'
import { useUserSearch } from 'src/hooks/useUserSearch'
import Trns, { useTranslate } from 'src/localization/Trns'
import {
  CreditCardDisplayStatus,
  CreditCardListModel,
  CreditCardStatus,
} from 'src/models/creditCard.models'
import { UserFeature, UserListModel } from 'src/models/user.models'
import { connectedPush } from 'src/navigation/Lnk'
import { accountOverviewUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectMyWalletPhysicalCardsMatch } from 'src/redux/selectors/router.selectors'
import variables from 'src/style/variables.json'
import { convertEnumToSentenceCase } from 'src/utils/string.utils'
import { css } from 'styled-components'

interface PhysicalCardsTableProps {}

interface PhysicalCardsTableFilters {
  search: string
  displayStatuses: CreditCardDisplayStatus[]
  userIds?: string[]
}

const PhysicalCardsTable: React.FC<
  PhysicalCardsTableProps
> = (): React.ReactElement => {
  const user = useSelector(selectUserListModelFromState)
  const myWalletPhysicalCardsMatch = useSelector(
    selectMyWalletPhysicalCardsMatch,
  )

  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  const history = useHistory()
  const dispatch = useDispatch()
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const userDropdownProps = useUserSearch({
    filters: {
      inOrganization: true,
      count: 10,
    },
    selectedValues: selectedUsers,
    onSelectionChange: (value) => {
      const newSelectedUsers = new Set(selectedUsers)
      if (newSelectedUsers.has(value)) {
        newSelectedUsers.delete(value)
      } else {
        newSelectedUsers.add(value)
      }
      setSelectedUsers(newSelectedUsers)
      handleFilterChange({ userIds: Array.from(newSelectedUsers) })
    },
    placeholder: translate('Filter by user'),
    isMulti: true,
  })

  // Define extended model with additional stats properties
  interface PhysicalCardsDataGridRowModel extends GridValidRowModel {
    creditCard: ExtendedCreditCardListModel
    index: number
  }

  // Update URL when filters change
  const updateUrl = useCallback(
    (newFilters: PhysicalCardsTableFilters) => {
      const params = new URLSearchParams()
      if (newFilters.search) params.set('keyword', newFilters.search)
      if (newFilters.displayStatuses.length) {
        newFilters.displayStatuses.forEach((status) => {
          params.append('displayStatuses', status)
        })
      }
      if (newFilters.userIds?.length) {
        // Remove any existing user parameters
        params.delete('user')
        // Add each user ID as a separate user parameter
        newFilters.userIds.forEach((userId) => {
          params.append('user', userId)
        })
      }
      history.push({
        search: params.toString(),
      })
    },
    [history],
  )

  // Initialize filters from URL or default values
  const [filters, setFilters] = useState<PhysicalCardsTableFilters>(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const strStatuses: string[] =
      searchParams.get('displayStatuses')?.split(',') || []
    const validStatuses = Object.values(CreditCardDisplayStatus)
    const filteredStatuses = strStatuses
      .filter((status) =>
        validStatuses.includes(status as CreditCardDisplayStatus),
      )
      .map((status) => status as CreditCardDisplayStatus)
    return {
      search: searchParams.get('keyword') || '',
      displayStatuses: filteredStatuses,
      userIds: searchParams.getAll('user') || undefined,
    }
  })

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  })

  const handleFilterChange = useCallback(
    (newFilters: Partial<PhysicalCardsTableFilters>) => {
      const updatedFilters = { ...filters, ...newFilters }
      setFilters(updatedFilters)
      // Remove debounce for search changes to ensure immediate updates
      if ('search' in newFilters) {
        updateUrl(updatedFilters)
      } else {
        // Keep debounce for other filter changes
        _.debounce(updateUrl, 500)(updatedFilters)
      }
    },
    [filters, updateUrl],
  )

  // Reset filters
  const handleResetFilters = useCallback(() => {
    const defaultFilters: PhysicalCardsTableFilters = {
      search: '',
      displayStatuses: [],
      userIds: undefined,
    }
    setFilters(defaultFilters)
    updateUrl(defaultFilters)
    setSelectedUsers(new Set())
  }, [updateUrl])

  const isDisabled = (creditCard: CreditCardListModel): boolean => {
    return creditCard.isInactive || creditCard.isClosed
  }

  const columns: Array<GridColDef<PhysicalCardsDataGridRowModel>> = [
    {
      field: 'issuerInfo',
      headerName: translate('Card Account'),
      flex: 16,
      sortable: false,
      renderCell: (params: any): React.ReactElement => {
        const creditCard = params.row.creditCard
        const disabled = isDisabled(creditCard)
        return (
          <CardGraphicAndNameCell
            text={creditCard.issuerName}
            last4={creditCard.partner?.isVirtualAccount ? undefined : creditCard.last4}
            cardTypeImageSrc={creditCard.cardImage.urls.small}
            cardTypeAlt={creditCard.issuer}
            bold
            size={'large'}
            disabled={disabled}
            params={params}
          />
        )
      },
    },
    {
      field: 'displayName',
      headerName: translate('Nickname'),
      flex: 16,
      sortable: false,
      renderCell: (params: any): React.ReactElement => {
        const creditCard = params.row.creditCard
        const disabled = isDisabled(creditCard)
        return (
          <DataGridCell
            text={creditCard.displayName}
            disabled={disabled}
            params={params}
          />
        )
      },
    },
    {
      field: 'owner',
      headerName: translate('Account Owner'),
      flex: 18,
      sortable: false,
      renderCell: (params: any): React.ReactElement => {
        const creditCard = params.row.creditCard
        const disabled = isDisabled(creditCard)
        const owner = creditCard.user
          ? new UserListModel(creditCard.user)
          : undefined
        if (!owner) {
          return <DataGridCell text={'-'} disabled={disabled} params={params} />
        }
        return (
          <UserAvatarCell user={owner} disabled={disabled} params={params} />
        )
      },
    },
    // TODO: add back in when we have the data
    // { field: 'whoHasAccess', headerName: translate('Who Has Access'), flex: 19 width: 200 },
    {
      field: 'status',
      headerName: translate('Status'),
      flex: 14,
      sortable: false,
      renderCell: (params: any): React.ReactElement => {
        const creditCard = params.row.creditCard
        let tagColor: PillProps['color']
        let statusText = translate(
          convertEnumToSentenceCase(creditCard.displayStatus),
        )
        switch (creditCard.displayStatus) {
          case CreditCardDisplayStatus.Active:
            tagColor = 'highlight'
            break
          case CreditCardDisplayStatus.Closed:
            tagColor = 'severe'
            break
          case CreditCardDisplayStatus.Pending:
            statusText = translate('Action Required')
            tagColor = 'severe'
            break
          case CreditCardDisplayStatus.Inactive:
          default:
            if (creditCard.isBlocked) {
              tagColor = 'severe'
            } else {
              tagColor = undefined
            }
        }
        const disabled = isDisabled(creditCard)
        return (
          <PillCell color={tagColor} disabled={disabled} params={params}>
            {statusText}
          </PillCell>
        )
      },
    },
    hasVirtualCardFeatures && {
      field: 'budgetsCount',
      headerName: translate('Budgets'),
      flex: 14,
      sortable: false,
      renderCell: (params: any): React.ReactElement => {
        const creditCard = params.row.creditCard
        const text = creditCard.partner?.virtualCardsSupported
          ? creditCard.numBudgets
          : translate('N/A')
        return <DataGridCell text={text} params={params} />
      },
    },
    hasVirtualCardFeatures && {
      field: 'virtualCardsCount',
      headerName: translate('Virtual Cards'),
      flex: 14,
      sortable: false,
      renderCell: (params: any): React.ReactElement => {
        const creditCard = params.row.creditCard
        const text = creditCard.partner?.virtualCardsSupported
          ? creditCard.numVcns
          : translate('N/A')
        return <DataGridCell text={text} params={params} />
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      hideable: false,
      resizable: false,
      sortable: false,
      renderCell: (params: any): React.ReactElement => {
        const creditCard = params.row.creditCard as CreditCardListModel
        const disabled = isDisabled(creditCard)
        return (
          <ActionsCell
            actionsComponent={<SvgMoreIcon />}
            id={creditCard.id}
            disabled={disabled}
            params={params}
            showVirtualCardFeatures={
              creditCard.status !== CreditCardStatus.NotApplicable
            }
          />
        )
      },
      renderHeader: () => <></>,
    },
  ]
    // IMPORTANT NOTE
    // .filter(Boolean) will remove any false-y statements (false/undefined) from this array
    // this means that because of the conditional column calls, we can safely
    // cast this to this type here.
    // Knowing that the tables will be rebuilt to a more agnostic form that will
    // account for conditional column rendering, I am a little hesitant to make this
    // perfect and because of deadlines and an upcoming launch, we are a little
    // pressed for time here. Use "any" for now is OK to get this across the finish line.
    // Any questions or concerns, please talk to @bjunya-extend - happy to work with anyone
    // on this
    .filter(Boolean) as Array<GridColDef<PhysicalCardsDataGridRowModel>>

  const columnsSafe = useMemo(
    () =>
      columns.filter(
        (column): column is GridColDef<PhysicalCardsDataGridRowModel> =>
          column !== undefined,
      ),
    [columns],
  )

  const {
    creditCardsData,
    isCreditCardsLoading,
    creditCardsRows: extendedCreditCards,
    isSuccess,
  } = useCreditCardsWithStats({
    filters: {
      search: filters.search,
      displayStatuses: filters.displayStatuses,
    },
    paginationModel,
    userId: myWalletPhysicalCardsMatch
      ? user?.id
      : selectedUsers.size > 0
        ? Array.from(selectedUsers)
        : undefined,
  })

  const creditCardsRows: PhysicalCardsDataGridRowModel[] | undefined =
    useMemo(() => {
      return extendedCreditCards?.map((creditCard, index) => {
        return {
          id: creditCard.id,
          creditCard,
          index,
        }
      })
    }, [extendedCreditCards])

  // Filter flyout props
  const filtersFlyoutProps: FiltersFlyoutProps = {
    title: translate('Physical Card Filters'),
    onReset: handleResetFilters,
    loading: isCreditCardsLoading,
    filters: (
      <>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          <div
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Card Status')}
          </div>
          {Object.values(CreditCardDisplayStatus).map((status) => (
            <div key={status}>
              <input
                type="checkbox"
                data-testid={`${status.toLowerCase()}Chk`}
                checked={filters.displayStatuses.includes(status)}
                onChange={(): void => {
                  const newStatuses = filters.displayStatuses.includes(status)
                    ? filters.displayStatuses.filter((s) => s !== status)
                    : [...filters.displayStatuses, status]
                  handleFilterChange({ displayStatuses: newStatuses })
                }}
              />
              <span
                css={css`
                  margin-left: 8px;
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {convertEnumToSentenceCase(status)}
              </span>
            </div>
          ))}
        </div>
      </>
    ),
  }

  const NoResultsMessage = (): ReactElement => (
    <div
      css={css`
        padding-top: 40px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      `}
    >
      {translate('No results found. Please check your filters and try again.')}
    </div>
  )

  return (
    <Box noPadding noShadow outlineBorder>
      <UtilityToolbar
        header={
          <span
            css={css`
              ${getFontCss(
                variables.units['base-unit-5'],
                themeConstants.colors.highlight,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            <Trns
              source={'${count} Active'}
              expressions={{
                count: `${creditCardsData?.creditCards?.filter((card) => card.isActive).length || 0}`,
              }}
            />
          </span>
        }
        searchProps={{
          value: filters.search,
          onChange: (val: string) => handleFilterChange({ search: val }),
          placeholder: translate('Search by card nickname'),
        }}
        userDropdownProps={
          myWalletPhysicalCardsMatch ? undefined : userDropdownProps
        }
        filtersFlyoutProps={filtersFlyoutProps}
      />
      {isSuccess || isCreditCardsLoading ? (
        <DataGrid
          rows={isSuccess && !isCreditCardsLoading ? creditCardsRows : []}
          columns={columnsSafe}
          infiniteScrollProps={{
            complete: !isCreditCardsLoading,
            loading: isCreditCardsLoading,
            fetch: () => {}, // nothing because no pagination,
          }}
          filtersBody={{}}
          rowHeight={DataGridRowHeight.Comfy}
          rowCount={creditCardsData?.totalItems}
          paginationMode={'server'}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[]} // need to pass an empty array to disable pagination options
          pagination
          slots={{
            loadingOverlay: () => <LoadingBackgroundPage />,
            noResultsOverlay: () => <NoResultsMessage />,
            noRowsOverlay: () => <NoResultsMessage />,
          }}
          onRowClick={(row): void => {
            const physicalCardId = row.id.toString()
            dispatch(
              connectedPush({
                pathname: accountOverviewUrl(physicalCardId),
                search: '',
                hash: '',
                state: {},
              }),
            )
            analytics?.track(
              'virtual cards - select virtual card - item press',
              {
                intent: 'select virtual card',
                virtualCardSelected: physicalCardId,
                interaction: 'item press',
              },
            )
          }}
          initialState={{
            pinnedColumns: {
              right: ['actions'],
            },
          }}
        />
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding-bottom: 16px;
          `}
        >
          {translate('There was an error. Please try again')}
        </div>
      )}
    </Box>
  )
}

export default PhysicalCardsTable
